import React, { createContext, useContext, useState, useEffect } from 'react';

// =============================================================================
// Theme Context - Manages theme (lazlo/mauve/slate/mono) and mode (light/dark)
// =============================================================================

const ThemeContext = createContext(null);

const STORAGE_KEY = 'smart-sheets-theme';
const DEFAULT_THEME = 'mono';
const DEFAULT_MODE = 'light';

// Available themes and modes
export const THEMES = [
	{ id: 'lazlo', name: 'Lazlo', description: 'Purple/Violet - Default' },
	{ id: 'mauve', name: 'Mauve', description: 'Warm violet - Premium feel' },
	{ id: 'slate', name: 'Slate', description: 'Cyan - Developer/technical' },
	{ id: 'mono', name: 'Mono', description: 'Charcoal + Sunset accents' },
];

export const MODES = [
	{ id: 'light', icon: '☀️', label: 'Light' },
	{ id: 'dark', icon: '🌙', label: 'Dark' },
];

export const ThemeProvider = ({ children }) => {
	// Initialize from localStorage or defaults
	const [theme, setTheme] = useState(() => {
		try {
			const saved = localStorage.getItem(STORAGE_KEY);
			if (saved) {
				const parsed = JSON.parse(saved);
				return parsed.theme || DEFAULT_THEME;
			}
		} catch (e) {
			console.warn('Error reading theme from localStorage:', e);
		}
		return DEFAULT_THEME;
	});

	const [mode, setMode] = useState(() => {
		try {
			const saved = localStorage.getItem(STORAGE_KEY);
			if (saved) {
				const parsed = JSON.parse(saved);
				return parsed.mode || DEFAULT_MODE;
			}
		} catch (e) {
			console.warn('Error reading mode from localStorage:', e);
		}
		return DEFAULT_MODE;
	});

	// Apply theme classes to document root
	useEffect(() => {
		const root = document.documentElement;

		// Remove all theme and mode classes
		THEMES.forEach(t => root.classList.remove(`theme-${t.id}`));
		MODES.forEach(m => root.classList.remove(`mode-${m.id}`));

		// Add current theme and mode classes
		root.classList.add(`theme-${theme}`);
		root.classList.add(`mode-${mode}`);

		// Update theme-color meta tag for browser chrome/status bar
		const themeColors = {
			lazlo: { light: '#f0f2f7', dark: '#0e0b14' },
			mauve: { light: '#faf8fc', dark: '#1a1520' },
			slate: { light: '#f0f4f8', dark: '#0f172a' },
			mono: { light: '#fafafa', dark: '#09090b' },
		};
		const colors = themeColors[theme] || themeColors.mono;
		const themeColor = mode === 'dark' ? colors.dark : colors.light;

		// Remove media-query based theme-color tags from index.html
		document.querySelectorAll('meta[name="theme-color"][media]').forEach(el => el.remove());

		// Find or create the theme-color meta tag
		let meta = document.querySelector('meta[name="theme-color"]:not([media])');
		if (!meta) {
			meta = document.createElement('meta');
			meta.name = 'theme-color';
			document.head.appendChild(meta);
		}
		meta.content = themeColor;
	}, [theme, mode]);

	// Persist to localStorage
	useEffect(() => {
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify({ theme, mode }));
		} catch (e) {
			console.warn('Error saving theme to localStorage:', e);
		}
	}, [theme, mode]);

	const value = {
		theme,
		mode,
		setTheme,
		setMode,
		// Convenience toggles
		toggleMode: () => setMode(m => m === 'light' ? 'dark' : 'light'),
		isDark: mode === 'dark',
		isLight: mode === 'light',
	};

	return (
		<ThemeContext.Provider value={value}>
			{children}
		</ThemeContext.Provider>
	);
};

// Hook to use theme context
export const useTheme = () => {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error('useTheme must be used within a ThemeProvider');
	}
	return context;
};

export default ThemeContext;
